import { Feature } from '../analytics';
import { generateReplicantChatbotPayload } from '../chatbot/messageTemplates';
import createAsyncGetters from '../config/asyncGetters/createAsyncGetters';
import { getBuruFriends } from '../friends/friends.getters';
import { getLocale } from '../getters/user';

export type InactiveFriendData = {
  id: string;
  name: string;
  photo: string;
  updatedAt: number;
};

export const giftsAsyncGetters = createAsyncGetters({
  /**
   * Get a list of friends that are inactive beyond the minimum duration
   */
  getInactiveFriends: async (duration: number, api) => {
    const result: {
      id: string;
      profileName?: string;
      profilePhoto?: string;
      publicProfileName: string;
      publicProfilePhoto: string;
      hasPublicProfile: boolean;
      updatedAt: number;
    }[] = [];

    const state = await api.getOwnState();

    const filteredFriends = getBuruFriends(state);

    // Query inactive friends
    const friends = await api.searchPlayers({
      where: {
        // Only friends
        id: {
          isOneOf: filteredFriends,
        },
        // Exclude players who have been active within the threshold
        updatedAt: {
          lessThanOrEqual: api.date.now() - duration,
        },
      },
      sort: [{ field: 'updatedAt', order: 'asc' }],
      fields: [
        'profileName',
        'profilePhoto',
        'hasPublicProfile',
        'publicProfileName',
        'publicProfilePhoto',
        'updatedAt',
      ],
      // Only 20 friends
      limit: 20,
    });

    // Collect friends ids first
    result.push(...friends.results);

    // Convert to FriendListData
    const data = Object.values(result).map((player) => {
      const privateName = player.profileName;
      const privatePhoto = player.profilePhoto;

      const name = player.hasPublicProfile
        ? player.publicProfileName
        : privateName;
      const photo = player.hasPublicProfile
        ? player.publicProfilePhoto
        : privatePhoto;

      return {
        id: player.id,
        name,
        photo,
        updatedAt: player.updatedAt,
      } as InactiveFriendData;
    });

    return data;
  },

  getSendGiftsOaMessageDetails: async (
    args: { receiverIds: string[]; subFeature: string },
    api,
  ) => {
    if (args.receiverIds.length === 0) return [];

    const search = await api.searchPlayers({
      where: {
        id: { isOneOf: args.receiverIds },
      },
      fields: ['locale'],
      limit: args.receiverIds.length,
    });

    // Identical for every receiver in the batch, so it is built once.
    const payload = generateReplicantChatbotPayload({
      feature: Feature.GIFTS._,
      $subFeature: args.subFeature,
    });

    return search.results.map((player) => ({
      id: player.id,
      locale: getLocale({ settings: { locale: player.locale } }),
      payload,
    }));
  },
});
